import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api, { API } from "../lib/api";
import { Search, Plus, X, Phone, Loader2, UserPlus, Upload, Download, FileSpreadsheet, CheckCircle2 } from "lucide-react";

const NewPatientModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", age: "", gender: "", address: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.age) payload.age = parseInt(payload.age, 10) || null;
      else payload.age = null;
      const res = await api.post("/patients", payload);
      onCreated(res.data);
      onClose();
      setForm({ name: "", phone: "", email: "", age: "", gender: "", address: "", notes: "" });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create patient");
    } finally {
      setBusy(false);
    }
  };

  const fld = (label, key, type = "text", required = false) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] focus:border-transparent"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800">Add New Patient</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {fld("Full Name", "name", "text", true)}
          <div className="grid grid-cols-2 gap-3">
            {fld("Phone", "phone", "tel", true)}
            {fld("Email", "email", "email")}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {fld("Age", "age", "number")}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Gender</label>
              <select
                value={form.gender}
                onChange={(e) => set("gender", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
              >
                <option value="">Select</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          {fld("Address", "address")}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
            />
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
            <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-70 text-white rounded-md font-medium flex items-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />}
              Save Patient
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Patients = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const nav = useNavigate();

  const load = async (query = "") => {
    setLoading(true);
    try {
      const res = await api.get("/patients", { params: query ? { q: query } : {} });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Patients</h1>
          <p className="text-slate-500 text-sm mt-1">Manage your patient records</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={async () => {
              setExporting(true);
              try {
                const res = await api.get("/patients-export.csv", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `patients_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch {
                alert("Export failed");
              } finally { setExporting(false); }
            }}
            disabled={exporting}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2.5 rounded-md text-sm font-medium disabled:opacity-60"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2.5 rounded-md text-sm font-medium"
          >
            <Upload size={14} /> Import
          </button>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white px-4 py-2.5 rounded-md text-sm font-semibold"
          >
            <Plus size={16} /> Add Patient
          </button>
        </div>
      </div>

      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, phone, UHID or email..."
          className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
        />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <UserPlus size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm mb-3">{q ? "No patients match your search" : "No patients yet"}</p>
            {!q && (
              <button onClick={() => setOpen(true)} className="bg-[#0F2A5F] hover:bg-[#0A1E4A] text-white text-sm px-4 py-2 rounded-md">
                Add first patient
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3">UHID</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Age</th>
                <th className="px-4 py-3">Gender</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id} onClick={() => nav(`/app/patients/${p.id}`)} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3 text-sm font-mono text-slate-700">{p.uhid}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-800">{p.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600"><span className="inline-flex items-center gap-1"><Phone size={12} /> {p.phone}</span></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{p.age || "-"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{p.gender || "-"}</td>
                  <td className="px-4 py-3 text-sm text-[#0F2A5F]">Open →</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewPatientModal open={open} onClose={() => setOpen(false)} onCreated={(p) => setItems((prev) => [p, ...prev])} />
      <ImportPatientsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(patients) => setItems((prev) => [...patients, ...prev])}
      />
    </div>
  );
};

const ImportPatientsModal = ({ open, onClose, onImported }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  if (!open) return null;

  const doUpload = async (file) => {
    if (!file) return;
    setErr(""); setResult(null); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("dotmedy_token");
      const res = await axios.post(`${API}/patients/import`, fd, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data",
        },
      });
      setResult(res.data);
      if (res.data.imported > 0 && res.data.patients?.length) {
        onImported?.(res.data.patients);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const closeAll = () => { setResult(null); setErr(""); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-[#14B8A6]" />
            <h3 className="font-semibold text-slate-800">Import Patients</h3>
          </div>
          <button onClick={closeAll} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {result ? (
            <>
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded p-3">
                <CheckCircle2 size={18} />
                <div className="text-sm">
                  <strong>{result.imported}</strong> patient(s) imported.
                  {result.skipped > 0 && <> <strong>{result.skipped}</strong> skipped.</>}
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded p-3">
                  <div className="text-xs font-semibold text-amber-800 mb-1">Errors:</div>
                  <ul className="text-xs text-amber-900 space-y-0.5 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </div>
              )}
              <button onClick={closeAll} className="w-full bg-[#14B8A6] hover:bg-[#0F8A7D] text-white font-semibold py-2 rounded-md">Done</button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Upload a <strong>.csv</strong>, <strong>.xls</strong> or <strong>.xlsx</strong> file. Required columns:
                <span className="mx-1 font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">name</span>,
                <span className="mx-1 font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">phone</span>.
                Optional: <span className="font-mono text-xs">email, age, gender, address, notes</span>.
              </p>
              <p className="text-xs text-slate-500">
                Column headers are case-insensitive. UHID is auto-generated for every row.
              </p>

              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={(e) => doUpload(e.target.files?.[0])}
                className="hidden"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="w-full border-2 border-dashed border-slate-300 hover:border-[#14B8A6] rounded-lg py-10 text-center transition-colors"
              >
                <Upload size={26} className="mx-auto text-slate-400 mb-2" />
                {busy ? (
                  <div className="text-sm text-slate-600 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Importing...</div>
                ) : (
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Click to choose file</div>
                    <div className="text-xs text-slate-500 mt-1">.csv, .xls or .xlsx up to 5 MB</div>
                  </div>
                )}
              </button>

              {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}

              <details className="text-xs text-slate-600 border border-slate-200 rounded p-3">
                <summary className="cursor-pointer font-semibold text-slate-700">Sample CSV format</summary>
                <pre className="mt-2 bg-slate-50 p-2 rounded overflow-x-auto text-[11px]">
{`name,phone,email,age,gender,address,notes
John Doe,9876543210,john@example.com,32,Male,Mumbai,Regular patient
Jane Smith,9876500001,,28,Female,Delhi,`}
                </pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Patients;
